import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import {
  Button,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
  UncontrolledTooltip,
} from 'reactstrap';

import FileResizer from 'react-image-file-resizer';
import {
  clearFiles,
  createFileVersion,
  fetchFiles,
  updateFilesOnFilesList,
  updateFilesOnSelectedFilesList,
} from '../../../actions/Files';
import {
  runFileAction,
  setMiniActiveFile,
  setMiniOpenedFiles,
  showModalMini,
} from '../../../actions/Mini';

import {
  sendFigmaPostMessage,
} from '../../../helpers/Figma';
import { findAction } from '../../../helpers/Mini';
import { fileObject } from '../../../helpers/Uploader';

const FigmaSave = ({
  miniConfig,
  runAction,
  refreshFiles,
  openModal,
}) => {
  const [loading, setLoading] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [framesEnabled, setFramesEnabled] = useState(false);
  const [selectionEnabled, setSelectionEnabled] = useState(false);
  const [imagesEnabled, setImagesEnabled] = useState(false);

  const saveFrames = async () => {
    openModal(
      'MiniFileUploadExportSettingsModal',
      {
        className: 'modal-export-settings',
        saveType: 'save-frames',
      },
    );
  };

  const saveSelection = async () => {
    openModal(
      'MiniFileUploadExportSettingsModal',
      {
        className: 'modal-export-settings',
        saveType: 'save-selection',
      },
    );
  };

  const saveAllPageImages = async () => {
    openModal(
      'MiniFileUploadExportSettingsModal',
      {
        className: 'modal-export-settings',
        saveType: 'save-images',
      },
    );
  };

  const handleMessage = useCallback(async (message) => {
    const { data } = message;

    if (data.type !== 'sent-from-figma-to-host') {
      return;
    }

    const { name } = data;

    if (name === 'settle-menu') {
      const { frames, selection, images } = data.data;

      setFramesEnabled(frames);
      setSelectionEnabled(selection);
      setImagesEnabled(images);
    }

    if (name === 'save-get-data') {
      const { files, saveType } = data.data;
      if (showWarning) {
        setShowWarning(true);
      }
      const action = findAction(miniConfig.actions, `save-${saveType}`);
      runAction(null, action, null, files);
    }

    if (name === 'save-open-modal') {
      const { files } = data.data;

      openModal(
        'MiniFileUploadModal',
        {
          className: 'modal-upload',
          files: files.map(f => ({ ...fileObject, ...f })),
          showWarning,
          beforeClose: () => {
            setLoading(false);
            refreshFiles();
          },
        },
      );
    }

    if (name === 'resize-image') {
      const {
        file, extension, name: fileName, width, height,
      } = data.data;
      const tempFileName = extension === 'png' ? 'image.png' : 'image.jpg';
      const fileType = extension === 'png' ? 'image/png' : 'image/jpeg';

      const blob = new Blob([file], { type: fileType });

      const blobFile = new File([blob], tempFileName, { type: fileType });

      FileResizer.imageFileResizer(
        blobFile,
        4096,
        4096,
        extension === 'png' ? 'PNG' : 'JPEG',
        100,
        0,
        async (resizedImage) => {
          if (typeof resizedImage === 'string') {
            const response = await fetch(resizedImage);
            const arrayBuffer = await response.arrayBuffer();
            sendFigmaPostMessage('insert-resized-image', {
              file: new Uint8Array(arrayBuffer), name: fileName, width, height,
            });
          } else if (resizedImage instanceof Blob) {
            const arrayBuffer = await resizedImage.arrayBuffer();
            sendFigmaPostMessage('insert-resized-image', {
              file: new Uint8Array(arrayBuffer), name: fileName, width, height,
            });
          }
        },
        'blob',
      );
    }
  }, [miniConfig.actions, openModal, refreshFiles, runAction, showWarning]);

  useEffect(() => {
    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [handleMessage]);

  useEffect(() => {
    sendFigmaPostMessage('handle-init', {});
  }, []);

  if (miniConfig.integrationId !== 'figma') return null;

  return (
    <div className="figma-save-container">
      <UncontrolledDropdown
        group
        disabled={loading}
        size="sm"
        className="integration-save-dropdown"
      >
        <Button
          id="FigmaSaveDropdown"
          color="secondary"
          onClick={() => {
            saveFrames();
          }}
          disabled={loading || !framesEnabled}
          size="sm"
        >
          <span className="integration-save-dropdown-icon">
            <i className="far fa-save" />
          </span>
          <span className="integration-save-dropdown-label">
            Export Frames
          </span>
        </Button>
        <UncontrolledTooltip
          placement="left"
          target="FigmaSaveDropdown"
          className="md-up"
        >
          Export Frames
        </UncontrolledTooltip>
        <DropdownToggle
          caret
          color="secondary"
        />
        <DropdownMenu>
          <DropdownItem
            disabled={!selectionEnabled}
            onClick={() => {
              saveSelection();
            }}
            id="SaveSelectionOption"
          >
            Export selected
          </DropdownItem>
          <DropdownItem
            disabled={!imagesEnabled}
            onClick={() => {
              saveAllPageImages();
            }}
            id="SaveImagesOption"
          >
            Export images
          </DropdownItem>
        </DropdownMenu>
      </UncontrolledDropdown>
    </div>
  );
};

const mapStateToProps = state => ({
  collection: state.Collections.collection,
  sidebarOpen: state.Mini.sidebarOpen,
  miniConfig: state.Mini.config,
});

const mapDispatchToProps = dispatch => ({
  runAction: (file, action, event = null, files = []) => {
    dispatch(runFileAction(file, action, event, files));
  },
  handleCreateVersion: (fileUuid, params) => (
    dispatch(createFileVersion(fileUuid, params))
  ),
  refreshFiles: () => {
    dispatch(clearFiles());
    dispatch(fetchFiles());
  },
  openModal: (modalName, params) => {
    dispatch(showModalMini(modalName, params));
  },
  updateFileOnList: (file) => {
    dispatch(updateFilesOnFilesList([file]));
    dispatch(updateFilesOnSelectedFilesList([file]));
  },
  handleSetMiniOpenedFiles: (openedFiles) => {
    dispatch(setMiniOpenedFiles(openedFiles));
  },
  handleActiveFile: (fileUuid) => {
    dispatch(setMiniActiveFile(fileUuid));
  },
});

FigmaSave.propTypes = {
  miniConfig: PropTypes.object.isRequired,
  runAction: PropTypes.func.isRequired,
  refreshFiles: PropTypes.func.isRequired,
  openModal: PropTypes.func.isRequired,
};

FigmaSave.defaultProps = {

};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(FigmaSave));
